import {
  createPopup,
  type PopupTarget,
} from '@blocksuite/affine-components/context-menu';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ViewIcon } from '@blocksuite/icons/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { css } from '@emotion/css';
import { computed } from '@preact/signals-core';
import { html, type TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

import { dataViewI18nT } from '../../../../core/i18n.js';
import { dragHandler } from '../../../../core/utils/wc-dnd/dnd-context.js';
import { defaultActivators } from '../../../../core/utils/wc-dnd/sensors/index.js';
import {
  createSortContext,
  sortable,
} from '../../../../core/utils/wc-dnd/sort/sort-context.js';
import { verticalListSortingStrategy } from '../../../../core/utils/wc-dnd/sort/strategies/index.js';
import {
  type GanttAssigneeVariant,
  type GanttBarDisplayItem,
  type GanttBarDisplayKind,
} from '../../define.js';
import {
  DEFAULT_BAR_DISPLAY_ITEMS,
  type GanttSingleView,
} from '../../gantt-view-manager.js';

const KIND_LABEL_KEYS: Record<
  GanttBarDisplayKind,
  Parameters<typeof dataViewI18nT>[0]
> = {
  title: 'gantt.bar.title',
  assignee: 'gantt.bar.assignee',
  start: 'gantt.bar.start',
  end: 'gantt.bar.end',
  status: 'gantt.bar.status',
  progress: 'gantt.bar.progress',
};

/**
 * Open the bar-display configuration popover anchored to the given
 * trigger. The popover writes its changes through `view.setBar…`,
 * which routes into the Y.Doc view data — every edit persists.
 */
export function openBarDisplaySettings(
  target: PopupTarget,
  view: GanttSingleView
): void {
  const panel = document.createElement(
    'dv-gantt-bar-display-settings'
  ) as GanttBarDisplaySettings;
  panel.view = view;
  const close = createPopup(target, panel, { placement: 'bottom-end' });
  panel.onClose = close;
}

export class GanttBarDisplaySettings extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  @property({ attribute: false })
  accessor view!: GanttSingleView;

  @property({ attribute: false })
  accessor onClose: (() => void) | undefined;

  /**
   * Same reorder machinery the shared `data-view-properties-setting`
   * (kanban/table "show fields" dialog) uses, so the two dialogs have
   * identical drag affordance: a 4 px grab pill at the row's leading
   * edge, live translate of the dragged item under the cursor, and a
   * vertical-only constraint so the row can't stray off axis. The
   * HTML5 drag-drop + drop-indicator lines that this dialog used
   * before didn't match that pattern and drifted away from the
   * codebase's default reorder style.
   */
  private readonly items$ = computed(() =>
    this.view.barDisplayItems$.value.map(it => it.kind)
  );

  sortContext = createSortContext({
    activators: defaultActivators,
    container: this,
    onDragEnd: evt => {
      const over = evt.over;
      const activeId = evt.active.id;
      if (!over || over.id === activeId) return;
      const items = [...this.view.barDisplayItems$.value];
      const fromIdx = items.findIndex(it => it.kind === activeId);
      if (fromIdx < 0) return;
      const [moved] = items.splice(fromIdx, 1);
      if (!moved) return;
      let toIdx = items.findIndex(it => it.kind === over.id);
      if (toIdx < 0) return;
      // `fromIdx > toIdx` means the item moves UP the list — its
      // over-target's original index is unaffected by the splice
      // removal. `fromIdx < toIdx` means it moves DOWN, and after
      // removing the source the target index needs no +1 shift
      // because splice(fromIdx, 1) already decremented every index
      // after fromIdx by one. The shared properties-dialog uses the
      // same +1 branch; we mirror it so DRY hand-offs stay clean.
      if (fromIdx < toIdx) toIdx += 1;
      items.splice(Math.max(0, toIdx - (fromIdx < toIdx ? 1 : 0)), 0, moved);
      this.view.setBarDisplayItems(items);
    },
    modifiers: [
      // Lock the dragged translate to the Y-axis so the row can't
      // drift sideways off the list — matches the properties
      // dialog's feel, prevents the dragged item from overlapping
      // the visibility toggle / variant picker on the same row.
      ({ transform }) => ({ ...transform, x: 0 }),
    ],
    items: this.items$,
    strategy: verticalListSortingStrategy,
  });

  override connectedCallback(): void {
    super.connectedCallback();
    this.classList.add(panelStyle);
  }

  private items(): readonly GanttBarDisplayItem[] {
    return this.view.barDisplayItems$.value;
  }

  /**
   * Edits are always a full-array write because the items shape is
   * small and the Y.Doc diff stays trivial — simpler than tracking
   * per-item patches and keeps every mutation atomic.
   */
  private writeItems(next: GanttBarDisplayItem[]): void {
    this.view.setBarDisplayItems(next);
  }

  private readonly toggleVisible = (kind: GanttBarDisplayKind): void => {
    const next = this.items().map(it =>
      it.kind === kind ? { ...it, visible: !it.visible } : it
    );
    this.writeItems(next);
  };

  private readonly setVariant = (
    kind: GanttBarDisplayKind,
    variant: GanttAssigneeVariant
  ): void => {
    const next = this.items().map(it =>
      it.kind === kind ? { ...it, variant } : it
    );
    this.writeItems(next);
  };

  private readonly resetToDefaults = (): void => {
    this.writeItems([...DEFAULT_BAR_DISPLAY_ITEMS]);
  };

  override render(): TemplateResult {
    const items = this.items();
    return html`
      <div class=${headerStyle}>
        <div class=${titleStyle}>
          ${dataViewI18nT('gantt.barDisplay.title')}
        </div>
        <button
          class=${resetButtonStyle}
          @click=${this.resetToDefaults}
          title=${dataViewI18nT('gantt.barDisplay.resetToDefaults')}
        >
          ${dataViewI18nT('gantt.barDisplay.reset')}
        </button>
      </div>
      <div class=${subheaderStyle}>
        ${dataViewI18nT('gantt.barDisplay.description')}
      </div>
      <div class=${listStyle}>
        ${repeat(
          items,
          it => it.kind,
          item => this.renderRow(item)
        )}
      </div>
    `;
  }

  private renderRow(item: GanttBarDisplayItem): TemplateResult {
    // Title is mandatory — visibility button is rendered but
    // disabled so the eye stays on for the row. Everything else
    // respects the stored flag.
    const isTitle = item.kind === 'title';
    const label = dataViewI18nT(KIND_LABEL_KEYS[item.kind]);
    const visibilityLabel = item.visible
      ? dataViewI18nT('gantt.barDisplay.hideOnBars')
      : dataViewI18nT('gantt.barDisplay.showOnBars');
    return html`
      <div ${dragHandler(item.kind)} ${sortable(item.kind)} class=${rowStyle}>
        <div class=${dragBarStyle} aria-hidden="true"></div>
        <span class=${labelColStyle}>${label}</span>
        ${item.kind === 'assignee'
          ? this.renderVariantSegmented(item.variant ?? 'both')
          : html`<span class=${variantSpacerStyle}></span>`}
        <button
          class=${visibilityButtonStyle}
          data-visible=${item.visible ? 'true' : 'false'}
          ?disabled=${isTitle}
          title=${isTitle
            ? dataViewI18nT('gantt.barDisplay.titleAlwaysVisible')
            : visibilityLabel}
          aria-label=${visibilityLabel}
          @click=${() => !isTitle && this.toggleVisible(item.kind)}
        >
          ${ViewIcon()}
        </button>
      </div>
    `;
  }

  private renderVariantSegmented(
    current: GanttAssigneeVariant
  ): TemplateResult {
    const opts: ReadonlyArray<{ v: GanttAssigneeVariant; label: string }> = [
      { v: 'avatar', label: dataViewI18nT('gantt.barDisplay.variantAvatar') },
      { v: 'name', label: dataViewI18nT('gantt.barDisplay.variantName') },
      { v: 'both', label: dataViewI18nT('gantt.barDisplay.variantBoth') },
    ];
    return html`
      <div
        class=${segmentedStyle}
        role="radiogroup"
        aria-label=${dataViewI18nT('gantt.barDisplay.assigneeMode')}
      >
        ${opts.map(
          o => html`
            <button
              class=${segmentedButtonStyle}
              data-active=${current === o.v}
              role="radio"
              aria-checked=${current === o.v}
              @click=${() => this.setVariant('assignee', o.v)}
            >
              ${o.label}
            </button>
          `
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dv-gantt-bar-display-settings': GanttBarDisplaySettings;
  }
}

// --- Styles ---------------------------------------------------------------

// `position: absolute` is critical — floating-ui writes left/top via
// computePosition, but without `position: absolute` on the content
// element those writes are ignored and the popup renders at (0,0) of
// the modal overlay (i.e. top-left of the viewport). Every other
// popup in this codebase goes through popMenu which applies position
// in its own wrapper; this one builds its own panel, so the style
// has to set position itself.
const panelStyle = css({
  position: 'absolute',
  display: 'flex',
  flexDirection: 'column',
  minWidth: '280px',
  maxWidth: '340px',
  padding: '10px',
  background: 'var(--streamlain-v2-layer-background-primary)',
  border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
  borderRadius: '10px',
  boxShadow: 'var(--streamlain-v2-shadow-level2)',
  color: 'var(--streamlain-v2-text-primary)',
  fontSize: '13px',
});

const headerStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

// Sub-header hint sits under the title. Does double duty: names the
// two affordances in the dialog (drag + eye) so the ordinal numbers
// and drag cursor read as intentional, not accidental.
const subheaderStyle = css({
  fontSize: '11px',
  color: 'var(--streamlain-v2-text-secondary)',
  paddingBottom: '8px',
  marginBottom: '6px',
  borderBottom: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
});

const titleStyle = css({
  fontWeight: 600,
  fontSize: '14px',
});

const resetButtonStyle = css({
  appearance: 'none',
  border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
  background: 'transparent',
  color: 'var(--streamlain-v2-text-secondary)',
  borderRadius: '6px',
  padding: '2px 8px',
  fontSize: '11px',
  cursor: 'pointer',

  '&:hover': {
    background: 'var(--streamlain-v2-layer-background-hoverOverlay)',
    color: 'var(--streamlain-v2-text-primary)',
  },
});

const listStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '1px',
});

// Row hosts the wc-dnd drag handle + sortable. No drop-indicator
// lines or dragging opacity inlined here — the shared sort context
// translates the row under the cursor for a live-reorder preview,
// same as the properties dialog.
const rowStyle = css({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr auto auto',
  alignItems: 'center',
  gap: '8px',
  padding: '4px 6px',
  borderRadius: '6px',
  userSelect: 'none',

  '&:hover': {
    background: 'var(--streamlain-v2-layer-background-hoverOverlay)',
  },
});

// 4 × 12 px drag pill at the row's leading edge. Same dimensions as
// `.property-item-drag-bar` in the shared properties dialog so the
// two reorder UIs read as one system.
const dragBarStyle = css({
  width: '4px',
  height: '12px',
  borderRadius: '1px',
  background: '#efeff0',
  cursor: 'grab',

  '&:active': {
    cursor: 'grabbing',
  },
});

const labelColStyle = css({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const variantSpacerStyle = css({
  width: 0,
});

// Eye toggle. Visible → primary colour, hidden → dimmed strikethrough
// feel via reduced opacity. Triple-class (`&&&`) specificity beats
// the fullscreen-viewport override that otherwise forces the data-
// active colour back to text-secondary.
const visibilityButtonStyle = css({
  appearance: 'none',
  border: 'none',
  background: 'transparent',
  color: 'var(--streamlain-v2-text-secondary)',
  width: '24px',
  height: '24px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '16px',
  transition:
    'color 0.12s ease-out, background 0.12s ease-out, opacity 0.12s ease-out',

  '&:hover:not(:disabled)': {
    background: 'var(--streamlain-v2-layer-background-hoverOverlay)',
    color: 'var(--streamlain-v2-text-primary)',
  },

  '&[data-visible=false]': {
    opacity: 0.3,
  },

  '&&&[data-visible=true]': {
    color: 'var(--streamlain-v2-button-primary)',
  },

  '&:disabled': {
    cursor: 'not-allowed',
  },
});

// Segmented control for the assignee variant picker. Triple-class
// specificity (`&&&`) beats the viewport override so the selected
// state stays brand-coloured across themes.
const segmentedStyle = css({
  display: 'inline-flex',
  border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
  borderRadius: '6px',
  overflow: 'hidden',
});

const segmentedButtonStyle = css({
  appearance: 'none',
  border: 'none',
  background: 'transparent',
  color: 'var(--streamlain-v2-text-secondary)',
  padding: '2px 8px',
  fontSize: '11px',
  fontWeight: 500,
  cursor: 'pointer',
  borderRight: '1px solid var(--streamlain-v2-layer-insideBorder-border)',

  '&:last-child': { borderRight: 'none' },

  '&:hover': {
    background: 'var(--streamlain-v2-layer-background-hoverOverlay)',
    color: 'var(--streamlain-v2-text-primary)',
  },

  '&&&[data-active=true]': {
    background: 'var(--streamlain-v2-button-primary)',
    color: 'var(--streamlain-v2-button-pureWhiteText)',
  },
});
